import React, { useState } from "react";
import styled from "styled-components";
import { VarDef } from "./emailVars";
import { EmailBuilderActions } from "./useEmailBuilder";
import { PALETTE_BLOCKS, PALETTE_SYMBOLS } from "./paletteData";
import { MIME_BLOCK, MIME_VAR, MIME_SECTION, MIME_TEXT, encodeVar, setDragImagePill } from "./paletteDnd";

// ---------------------------------------------------------------------------
// Styled components (scoped to Palette)
// ---------------------------------------------------------------------------

const NAVY = "#005e9e";
const NAVY_LIGHT = "#e8f2fa";

const SearchWrap = styled.div`
	padding: 8px 10px 6px;
`;

const SearchInput = styled.input`
	width: 100%;
	padding: 7px 9px;
	border: 1px solid #e3e8ef;
	border-radius: 8px;
	font-size: 12px;
	background: #f8fafc;

	&:focus {
		outline: none;
		border-color: ${NAVY};
		background: #fff;
	}
`;

const Group = styled.div`
	border-bottom: 1px solid #eef2f7;
`;

const GroupHeader = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	background: none;
	border: none;
	cursor: pointer;
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.05em;
	color: #94a3b8;
	text-transform: uppercase;
	padding: 10px 12px 6px;

	&:hover {
		color: #334155;
	}
`;

const Chevron = styled.span<{ open: boolean }>`
	display: inline-block;
	transition: transform 0.15s;
	transform: ${({ open }) => (open ? "rotate(0deg)" : "rotate(-90deg)")};
`;

const GroupBody = styled.div`
	padding: 0 8px 8px;
	display: flex;
	flex-direction: column;
	gap: 3px;
`;

const BlockItem = styled.button`
	display: flex;
	align-items: center;
	gap: 9px;
	border: 1px solid transparent;
	border-radius: 8px;
	padding: 8px 9px;
	font-size: 13px;
	font-weight: 500;
	color: #334155;
	background: none;
	cursor: grab;
	text-align: left;
	transition: background 0.12s, border-color 0.12s, color 0.12s;
	width: 100%;

	&:hover {
		background: ${NAVY_LIGHT};
		border-color: #cfe3f2;
		color: ${NAVY};
	}
	&:active {
		cursor: grabbing;
	}
`;

const BlockIcon = styled.span`
	font-size: 15px;
	color: #94a3b8;
	flex-shrink: 0;
	width: 18px;
	text-align: center;
`;

const VarItem = styled.button`
	display: flex;
	align-items: center;
	gap: 9px;
	border: 1px solid #eef2f7;
	border-radius: 8px;
	padding: 8px 10px;
	background: #fff;
	cursor: grab;
	text-align: left;
	width: 100%;
	transition: background 0.12s, border-color 0.12s, box-shadow 0.12s;

	&:hover {
		background: ${NAVY_LIGHT};
		border-color: ${NAVY};
		box-shadow: 0 1px 4px rgba(0, 94, 158, 0.12);
	}
	&:active {
		cursor: grabbing;
	}
`;

const VarLabel = styled.b`
	font-size: 13px;
	font-weight: 600;
	color: #1e293b;
	display: block;
	line-height: 1.3;
`;

const VarExample = styled.span`
	font-size: 11px;
	color: #64748b;
	font-family: ui-monospace, Menlo, Consolas, monospace;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	display: block;
	margin-top: 1px;
`;

const VarIcon = styled.span`
	font-size: 14px;
	color: #94a3b8;
	flex-shrink: 0;
	line-height: 1;
`;

const SymGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 5px;
	padding: 2px 0 4px;
`;

const SymBtn = styled.button`
	width: 30px;
	height: 30px;
	border: 1px solid #e3e8ef;
	border-radius: 7px;
	background: #fff;
	cursor: pointer;
	font-size: 15px;
	transition: background 0.12s, border-color 0.12s;

	&:hover {
		background: ${NAVY_LIGHT};
		border-color: ${NAVY};
	}
`;

const Hint = styled.div`
	font-size: 11px;
	color: #94a3b8;
	line-height: 1.5;
	padding: 8px 12px 10px;
	border-top: 1px solid #eef2f7;
	margin-top: 4px;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function matchesQuery(q: string, ...fields: string[]): boolean {
	if (!q) return true;
	const lower = q.toLowerCase();
	return fields.some((f) => f.toLowerCase().includes(lower));
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PaletteProps {
	catalog: VarDef[];
	actions: Pick<EmailBuilderActions, "addBlock">;
	/** Called (mousedown trick) to insert a var-chip at the canvas caret. */
	onInsertVar: (name: string, label: string) => void;
	/** Called (mousedown trick) to insert a plain symbol at the canvas caret. */
	onInsertSymbol: (ch: string) => void;
	/** Click fallback for the "Tiêu đề mục" item — insert into the selected summary. */
	onInsertSection: () => void;
	/** Called on the Palette's onMouseDown to capture caret before focus is lost. */
	onPaletteMouseDown: () => void;
}

// ---------------------------------------------------------------------------
// Palette component
// ---------------------------------------------------------------------------

export function Palette({
	catalog,
	actions,
	onInsertVar,
	onInsertSymbol,
	onInsertSection,
	onPaletteMouseDown,
}: PaletteProps): JSX.Element {
	const [query, setQuery] = useState("");
	const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
		blocks: true,
		vars: true,
		syms: true,
	});

	const q = query.trim();

	const toggleGroup = (key: string) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

	// Filter derived lists
	const filteredBlocks = PALETTE_BLOCKS.filter((b) => matchesQuery(q, b.label, b.type));
	const filteredVars = catalog.filter((v) => matchesQuery(q, v.n, v.d, v.s));
	const filteredSyms = PALETTE_SYMBOLS.filter((s) => matchesQuery(q, s));
	// "Tiêu đề mục" is a special palette item (a summary row, not a block type).
	const sectionMatches = matchesQuery(q, "Tiêu đề mục", "section", "heading");
	const blocksHasItems = filteredBlocks.length > 0 || sectionMatches;

	// Auto-expand groups that have matches when searching
	const blocksOpen = q ? blocksHasItems : openGroups["blocks"];
	const varsOpen = q ? filteredVars.length > 0 : openGroups["vars"];
	const symsOpen = q ? filteredSyms.length > 0 : openGroups["syms"];

	return (
		<div onMouseDown={onPaletteMouseDown}>
			<SearchWrap>
				<SearchInput
					type="text"
					placeholder="Tìm khối, biến, ký hiệu…"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					aria-label="Tìm kiếm palette"
				/>
			</SearchWrap>

			{/* ---- Blocks group ---- */}
			{(!q || blocksHasItems) && (
				<Group>
					<GroupHeader
						onClick={() => toggleGroup("blocks")}
						aria-expanded={blocksOpen}>
						<span>Khối</span>
						<Chevron open={blocksOpen}>▾</Chevron>
					</GroupHeader>
					{blocksOpen && (
						<GroupBody>
							{filteredBlocks.map((b) => (
								<BlockItem
									key={b.type}
									aria-label={b.label}
									draggable
									onDragStart={(e) => {
										e.dataTransfer.setData(MIME_BLOCK, b.type);
										e.dataTransfer.setData(MIME_TEXT, b.label);
										e.dataTransfer.effectAllowed = "copy";
										setDragImagePill(e, b.label);
									}}
									onClick={() => actions.addBlock(b.type)}>
									<BlockIcon>{b.icon}</BlockIcon>
									{b.label}
								</BlockItem>
							))}
							{/* "Tiêu đề mục" — drag INTO a summary block to insert a section heading
							    between rows. Not a block type; carries its own MIME payload. */}
							{sectionMatches && (
								<BlockItem
									aria-label="Tiêu đề mục"
									data-testid="palette-section-title"
									draggable
									onDragStart={(e) => {
										e.dataTransfer.setData(MIME_SECTION, "1");
										e.dataTransfer.setData(MIME_TEXT, "Tiêu đề mục");
										e.dataTransfer.effectAllowed = "copy";
										setDragImagePill(e, "Tiêu đề mục");
									}}
									onClick={onInsertSection}>
									<BlockIcon>§</BlockIcon>
									Tiêu đề mục
								</BlockItem>
							)}
						</GroupBody>
					)}
				</Group>
			)}

			{/* ---- Variables group ---- */}
			{(!q || filteredVars.length > 0) && (
				<Group>
					<GroupHeader
						onClick={() => toggleGroup("vars")}
						aria-expanded={varsOpen}>
						<span>Biến</span>
						<Chevron open={varsOpen}>▾</Chevron>
					</GroupHeader>
					{varsOpen && (
						<GroupBody>
							{filteredVars.map((v) => (
								<VarItem
									key={v.n}
									title={`\${${v.n}}`}
									draggable
									onDragStart={(e) => {
										e.dataTransfer.setData(MIME_VAR, encodeVar(v.n, v.d));
										e.dataTransfer.setData(MIME_TEXT, `\${${v.n}}`);
										e.dataTransfer.effectAllowed = "copy";
										setDragImagePill(e, v.d);
									}}
									onClick={() => onInsertVar(v.n, v.d)}
									aria-label={v.d}>
									<VarIcon>⠿</VarIcon>
									<div style={{ minWidth: 0 }}>
										<VarLabel>{v.d}</VarLabel>
										<VarExample>vd: {v.s || `\${${v.n}}`}</VarExample>
									</div>
								</VarItem>
							))}
						</GroupBody>
					)}
				</Group>
			)}

			{/* ---- Symbols group ---- */}
			{(!q || filteredSyms.length > 0) && (
				<Group>
					<GroupHeader
						onClick={() => toggleGroup("syms")}
						aria-expanded={symsOpen}>
						<span>Ký hiệu</span>
						<Chevron open={symsOpen}>▾</Chevron>
					</GroupHeader>
					{symsOpen && (
						<GroupBody>
							<SymGrid>
								{filteredSyms.map((s) => (
									<SymBtn
										key={s}
										title={s}
										aria-label={s}
										onMouseDown={(e) => e.preventDefault()}
										onClick={() => onInsertSymbol(s)}>
										{s}
									</SymBtn>
								))}
							</SymGrid>
						</GroupBody>
					)}
				</Group>
			)}

			<Hint>
				Kéo khối hoặc biến thả vào email — hoặc bấm để chèn nhanh (khối thêm vào cuối, biến/ký hiệu chèn tại vị
				trí con trỏ).
			</Hint>
		</div>
	);
}
